import {
  type AlgoliaEnvironmentSuffix,
  getAlgoliaIndex,
} from 'catalog-search-sdk/client';
import {
  CATALOG_MENU_HIERARCHY_NAME,
  FACET_SEPARATOR,
} from 'catalog-search-sdk/constants';
import type { AlgoliaProductHit } from 'catalog-search-sdk/types';
import { mapRefinementFromPathname } from 'catalog-search-sdk/url';
import {
  StrictUserDataSchema,
  UserDataSchema,
} from 'catalog-search-sdk/userData';
import clsx from 'clsx';
import { TypedEventBus } from 'dotcom-core/src/components/EventBus';
import {
  COUNTRY_COOKIE,
  COUNTRY_HEADER,
  DEFAULT_COUNTRY,
  STORE_CODE_DEFAULT,
} from 'dotcom-core/src/constants';
import { log } from 'dotcom-core/src/helpers/logServer';
import { pushDataEvent } from 'dotcom-core/src/helpers/tracking';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Link,
  data,
  generatePath,
  redirect,
  useLoaderData,
  useLocation,
  useSearchParams,
} from 'react-router';
import env from '~/env';
import type { Language } from '~/graphql';
import { ROUTE_SEGMENTS } from '~/helpers/app-routes';
import { ROUTE_PATHS } from '~/helpers/app-routes';
import { getIsAuthenticated, verifyUserSession } from '~/helpers/auth.server';
import { makeClient } from '~/helpers/client';
import { getCookie } from '~/helpers/cookie';
import { logSentryError } from '~/helpers/error';
import { useIntl } from '~/helpers/intl';
import { AudioPlayerProvider } from '../../components/AudioPlayer';
import type { Route } from './+types/route';
import { FilterMenu } from './FilterMenu';
import { FilterStateProvider } from './FilterStateContext';
import { ProductCard } from './ProductCard';
import { SearchBar } from './SearchBar';
import { parseSearchParams, searchClient, searchClientHelper } from './algolia';

function CatalogPage() {
  const {
    hits,
    isFirstPage,
    isLastPage,
    nbPages,
    page,
    storeCode,
    totalHits,
    query,
  } = useLoaderData<CatalogLoader>();
  const { t, tReact } = useIntl();
  const audioPlayerRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [currentPathname, setCurrentPathname] = useState(location.pathname);

  // When the pathname of the page change, we want to remove pagination search field.
  useEffect(() => {
    if (location.pathname !== currentPathname) {
      searchParams.delete('page');
      setSearchParams(searchParams);
      setCurrentPathname(location.pathname);
    }
  }, [searchParams, setSearchParams, location.pathname, currentPathname]);

  useRouteChangeListener();

  const [pageListShown, setPageListShown] = useState([page]);
  const [currentHits, setCurrentHits] = useState<AlgoliaProductHit[]>(hits);

  const getPageQuery = useCallback(
    (page: number) => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('page', String(page));
      return newSearchParams.toString();
    },
    [searchParams],
  );

  const { previousPage, nextPage } = useMemo(() => {
    let nextPage = Number.MIN_VALUE;
    let previousPage = Number.MAX_VALUE;

    pageListShown.forEach((p) => {
      if (p > nextPage) nextPage = p;
      if (p < previousPage) previousPage = p;
    });

    previousPage -= 1;
    nextPage += 1;

    return { previousPage, nextPage };
  }, [pageListShown]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: This hooks *must* only be re-render on page updates, trust me.
  useEffect(() => {
    const list =
      previousPage < page
        ? [...currentHits, ...hits]
        : [...hits, ...currentHits];

    setCurrentHits(
      list.filter(({ objectID }, index, array) => {
        return array.findIndex((val) => val.objectID === objectID) === index;
      }),
    );

    setPageListShown([...pageListShown, page]);
  }, [page]);

  // When the user is doing a sort, or a query search; we want to reset the pagination,
  // that's what this useEffect is doing.
  useEffect(() => {
    if (!searchParams.get('page')) {
      setCurrentHits(hits);
      setPageListShown([page]);
    }
  }, [searchParams, hits, page]);

  const shouldDisplayPrevious = !isFirstPage && previousPage >= 0;
  const shouldDisplayNextPage = !isLastPage && nextPage < nbPages;

  return (
    <>
      <div className="container py-3 lg:py-5 relative">
        <FilterStateProvider>
          <FilterMenu />
          <SearchBar />
        </FilterStateProvider>

        <AudioPlayerProvider audioPlayerRef={audioPlayerRef}>
          {shouldDisplayPrevious && (
            <div className="pb-6 text-center">
              <span
                data-testid="previous-pagination-count"
                className="text-xs text-grey-60"
              >
                {t('catalog.numberListing', {
                  fallback: '{value} products out of {totalHits}',
                  values: {
                    value: currentHits?.length,
                    totalHits,
                  },
                })}
              </span>
              <div className="flex justify-center pt-1">
                <Link
                  preventScrollReset={true}
                  className="webex-btn webex-btn--primary webex-btn--regular"
                  data-testid="load-previous-button"
                  to={{ search: getPageQuery(previousPage) }}
                >
                  {t('catalog.pagination.previous', {
                    fallback: 'Show previous results',
                  })}
                </Link>
              </div>
            </div>
          )}

          {totalHits === 0 && query ? (
            <div className="text-center py-10" data-testid="no-results-message">
              {tReact('catalog.emptyState', {
                fallback: 'There are no results for {querySearch}',
                values: {
                  querySearch: () => (
                    <span
                      data-testid="no-results-item-title"
                      className="font-medium"
                    >
                      {query}
                    </span>
                  ),
                },
              })}
            </div>
          ) : (
            <div
              className={clsx(
                'grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-y-3 gap-x-3 justify-items-center',
                'auto-rows-max',
              )}
            >
              {currentHits.map((hit, index) => (
                <ProductCard
                  index={index}
                  key={hit.objectID}
                  hit={hit}
                  storeCode={storeCode}
                />
              ))}
            </div>
          )}

          {shouldDisplayNextPage && (
            <div className="pt-6 text-center">
              <span
                data-testid="more-pagination-count"
                className="text-xs text-grey-60"
              >{`${currentHits.length} products out of ${totalHits}`}</span>

              <div className="flex justify-center pt-1">
                <Link
                  preventScrollReset={true}
                  className="webex-btn webex-btn--primary webex-btn--regular"
                  data-testid="load-more-button"
                  to={{ search: getPageQuery(nextPage) }}
                >
                  {t('components.ProductList.loadMore', {
                    fallback: 'Show more results',
                  })}
                </Link>
              </div>
            </div>
          )}
        </AudioPlayerProvider>
      </div>

      <div
        className="sticky w-full bottom-0 z-50"
        ref={audioPlayerRef}
        id="audio-player"
      />
    </>
  );
}

const useRouteChangeListener = () => {
  const location = useLocation();

  useEffect(() => {
    TypedEventBus.emit('ni-v2/route-changed', { route: location.pathname });
  }, [location]);
};

async function clientLoader(loaderArgs: Route.ClientLoaderArgs) {
  /*
   *  we only want to search again on the client side
   * further data can come from the server
   */
  const serverLoaderData = await loaderArgs.serverLoader();
  const catalogResults = await searchProducts({
    loaderArgs,
    licenseIDs: serverLoaderData?.licenseIDs,
    nativeId: serverLoaderData.nativeId,
    storeCode: serverLoaderData.storeCode,
    country: serverLoaderData.country,
  });

  return {
    ...serverLoaderData,
    ...catalogResults,
  };
}

function getCatalogSlugs(url: URL) {
  return (
    url.pathname.split(`${ROUTE_SEGMENTS.catalog}/`)[1]?.replace(/\/?$/, '/') ??
    ''
  );
}

export function checkIfOldPathnameAndRedirect({
  url,
  redirects,
}: {
  url: URL;
} & Pick<SearchSettings, 'redirects'>) {
  let redirectPathname = '';
  const currentPathname = getCatalogSlugs(url);

  Object.entries(redirects).forEach(([oldPathname, newPathname]) => {
    oldPathname = oldPathname.toLowerCase().replace(/\/?$/, '/');

    if (newPathname && currentPathname.toLowerCase() === oldPathname) {
      redirectPathname = newPathname;
    }
  });

  return redirectPathname;
}

export function isValidCatalogPathname({
  url,
  slugHierarchyFull,
}: {
  url: URL;
} & Pick<SearchSettings, 'slugHierarchyFull'>): boolean {
  // That's all available urls for the catalog.
  const availablePathnames = Object.values(slugHierarchyFull)
    .flatMap((s) => [...s])
    .map((s) => s.split(FACET_SEPARATOR).join('/').replace(/\/?$/, '/'));

  const currentPathname = getCatalogSlugs(url);

  return availablePathnames.includes(currentPathname);
}

async function loader(loaderArgs: Route.LoaderArgs) {
  const slugs = loaderArgs.params['*'];
  const DEFAULT_ROUTE = 'software';

  if (slugs === '') return redirect(`${DEFAULT_ROUTE}/`);

  let licenseIDs: string[] = [];
  const language = loaderArgs.params.language as Language;
  const cookieHeader = loaderArgs.request.headers.get('Cookie');

  const country =
    (getCookie(cookieHeader, COUNTRY_COOKIE) ||
      loaderArgs.request.headers.get(COUNTRY_HEADER)) ??
    DEFAULT_COUNTRY;

  const {
    countryStoreMapping,
    slugMapping,
    seoMapping,
    slugHierarchyFull,
    redirects,
  } = await getSearchSettings(language);

  const storeCode = countryStoreMapping[country] || STORE_CODE_DEFAULT;

  const url = new URL(loaderArgs.request.url);

  const newPathname = checkIfOldPathnameAndRedirect({ url, redirects });
  if (newPathname) {
    return redirect(
      `${generatePath(ROUTE_PATHS.catalog, { language })}/${newPathname}`,
    );
  }

  if (!isValidCatalogPathname({ url, slugHierarchyFull })) {
    throw new Response(null, { status: 404, statusText: 'Not Found' });
  }

  const isAuthenticated = getIsAuthenticated(loaderArgs.request);
  let nativeId = undefined;

  const headers = new Headers();

  headers.append(
    'Cache-Control',
    isAuthenticated
      ? 'private'
      : /*
         * for 15min, cache is assumed fresh.
         * between 15–30min, stale cache is used while a revalidation happens.
         * after 30min, cache is invalid and we hit the network.
         */
        'public',
  );

  if (isAuthenticated) {
    const client = makeClient(loaderArgs);
    try {
      const userOwnProductIds = await client.UserOwnProductIds();
      licenseIDs = userOwnProductIds.me?.licenseIDs ?? [];
      const { session } = await verifyUserSession(loaderArgs.request, headers);
      nativeId = session.get('user')?.nativeId;
    } catch (error) {
      if (error instanceof Error) {
        log.warn({ errorName: error.name, message: error.message });

        logSentryError({
          name: error.name || '',
          message: error.message,
          source: 'server',
        });
      }
    }
  }
  const menuRefinement = mapRefinementFromPathname(slugs || '');
  const searchResults = await searchProducts({
    loaderArgs,
    licenseIDs,
    nativeId,
    storeCode,
    country,
  });
  const seo = seoMapping[menuRefinement];

  return data(
    {
      ...searchResults,
      licenseIDs,
      nativeId,
      isAuthenticated,
      storeCode,
      country,
      slugMapping,
      slugHierarchyFull,
      seoTitle: seo?.seo_display_title,
      seoDescription: seo?.seo_display_description,
      seoImage: seo?.seo_display_image,
    },
    {
      headers,
    },
  );
}

async function searchProducts({
  loaderArgs,
  licenseIDs,
  nativeId,
  storeCode,
  country,
}: {
  loaderArgs: Route.LoaderArgs | Route.ClientLoaderArgs;
  licenseIDs: string[];
  nativeId?: string;
  storeCode: string;
  country: string;
}) {
  const { params, request } = loaderArgs;
  const language = params.language as Language;
  const url = new URL(request.url);
  const parsedParams = parseSearchParams(url.searchParams);

  const indexName = getAlgoliaIndex({
    language,
    sort: parsedParams.sort,
    // have to cast as envalid won't narrow to union of literals
    environment: env.ALGOLIA_ENV_SUFFIX as AlgoliaEnvironmentSuffix,
  });
  const searchClient = searchClientHelper(indexName);

  const filters = parsedParams.hideProductsYouOwn
    ? licenseIDs?.map((id) => `NOT licensedproduct_ids=${id}`).join(' AND ')
    : '';

  const slugs = params['*'] || '';
  const menuRefinement = mapRefinementFromPathname(slugs);

  const { content: response } = await searchClient.searchOnce({
    filters,
    clickAnalytics: true,
    enablePersonalization: true,
    page: parsedParams.page,
    query: parsedParams.query,
    maxValuesPerFacet: 100,
    hierarchicalFacetsRefinements: {
      [CATALOG_MENU_HIERARCHY_NAME]: [menuRefinement],
    },
    ruleContexts: [storeCode, country],
  });

  const {
    hits,
    queryID: queryId,
    page = 0,
    nbPages = 0,
    query,
    nbHits,
    hierarchicalFacets,
  } = response;

  const menuFacets = hierarchicalFacets.find(({ name }) => {
    return name === CATALOG_MENU_HIERARCHY_NAME;
  })?.data;

  /*
   * Virtual replicas, used in index sorting, may return fewer results
   * due to higher relevancy strictness.
   * So when a sorted hits amount is available, we prioritise that.
   */
  const totalHits = response.nbSortedHits || nbHits;

  pushDataEvent({
    event: 'catalog_search',
    queryId,
    query,
    indexName,
    nativeId,
    filters: menuRefinement,
  });

  return {
    // algoliasearch helper does not provide generic type for hits, so we cast
    // to avoid `any[]`
    hits: hits as AlgoliaProductHit[],
    queryId,
    isFirstPage: page === 0,
    isLastPage: page === nbPages || hits.length === totalHits,
    nbPages,
    totalHits,
    page,
    query,
    hideProductsYouOwn: parsedParams.hideProductsYouOwn,
    menuRefinement,
    menuFacets,
  };
}

type SearchSettings = Awaited<ReturnType<typeof getSearchSettings>>;

async function getSearchSettings(language: string) {
  const indexName = getAlgoliaIndex({
    language,
    // have to cast as envalid won't narrow to union of literals
    environment: env.ALGOLIA_ENV_SUFFIX as AlgoliaEnvironmentSuffix,
  });
  const settings = await searchClient.getSettings({ indexName });
  const { error } = StrictUserDataSchema.safeParse(settings.userData);

  if (error) {
    log.error(
      error,
      `Catalog search settings missing or malformed in index "${indexName}".`,
    );
  }

  const data = UserDataSchema.parse(settings.userData);

  return {
    countryStoreMapping: data.country_store_mapping,
    slugMapping: data.slug_mapping,
    seoMapping: data.seo_mapping,
    slugHierarchyFull: data.slug_hierarchy_full,
    redirects: data.redirects,
  };
}

function meta({ data, matches }: Route.MetaArgs) {
  const rootMeta = matches.find((match) => match?.id === 'root')?.meta ?? [];
  const { t } = useIntl();
  const fallbackTitle = t('components.Catalog.title', {
    fallback: 'Catalog',
  });
  const title = data?.seoTitle || fallbackTitle;

  return [
    ...rootMeta,
    { title },
    { name: 'description', content: data?.seoDescription || '' },
    { property: 'og:image', content: data?.seoImage || '' },
    { property: 'og:title', content: title },
    { property: 'og:description', content: data?.seoDescription || '' },
  ];
}

type CatalogLoader = typeof loader;

const headers = ({ loaderHeaders }: Route.HeadersArgs) => {
  return { 'Cache-Control': loaderHeaders.get('Cache-Control') };
};

export {
  meta,
  loader,
  clientLoader,
  headers,
  type CatalogLoader,
  type SearchSettings,
};

export default CatalogPage;
